import { type HomeBoard, MAX_HOME_BOARDS } from "@ronin/contracts";
import { Button, cn, Dialog, Input, Label, Tooltip } from "@ronin/ui";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { DragHandle, Sortable, useSortableItem } from "./sortable.js";
import type { HomeActions } from "./use-home.js";

/** spec12 FR5: add, rename, reorder and delete boards. */
export function BoardsDialog({
  open,
  onOpenChange,
  boards,
  activeId,
  onOpenBoard,
  actions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boards: HomeBoard[];
  activeId: string;
  onOpenBoard: (id: string) => void;
  actions: HomeActions;
}) {
  const id = useId();
  const [name, setName] = useState("");
  const byId = new Map(boards.map((b) => [b.id, b]));

  function add(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    // FR5: a new board opens at once (it starts with the Boards widget).
    actions.createBoard.mutate(trimmed, {
      onSuccess: (board) => {
        setName("");
        onOpenBoard(board.id);
        onOpenChange(false);
      },
    });
  }

  const full = boards.length >= MAX_HOME_BOARDS;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Boards"
      description="Each board is a tab of your dashboard. Drag a card to reorder, click a name to rename."
      className="w-[min(100%-2rem,48rem)]"
    >
      <Sortable
        ids={boards.map((b) => b.id)}
        label={(boardId) => byId.get(boardId)?.name ?? "Board"}
        onReorder={(ids) => actions.orderBoards.mutate(ids)}
        layout="grid"
      >
        <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {boards.map((board) => (
            <BoardCard
              key={board.id}
              board={board}
              main={board.main}
              active={board.id === activeId}
              onOpen={() => {
                onOpenBoard(board.id);
                onOpenChange(false);
              }}
              onRename={(next) => actions.renameBoard.mutate({ id: board.id, name: next })}
              onDelete={() => actions.removeBoard.mutate(board)}
            />
          ))}
          {/* FR5: a new board is one more card at the end. */}
          <li className="flex min-h-36 flex-col justify-between gap-3 border border-dashed border-input p-3">
            <form className="flex flex-1 flex-col justify-between gap-3" onSubmit={add}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${id}-new`}>New board</Label>
                <Input
                  id={`${id}-new`}
                  value={name}
                  maxLength={40}
                  className="h-8"
                  placeholder="e.g. Homelab"
                  disabled={full}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary" size="sm" disabled={!name.trim() || full}>
                <Plus />
                Add board
              </Button>
            </form>
            {full && (
              <p className="text-xs text-faint-foreground">At most {MAX_HOME_BOARDS} boards.</p>
            )}
          </li>
        </ul>
      </Sortable>
      <div className="flex justify-end border-t pt-4 pb-1">
        <Button type="button" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * One board as a card, side by side like the Boards widget: the open board is highlighted
 * (`bg-muted`, `border-input`). Drag handle and name on top, Open and Delete at the bottom.
 */
function BoardCard({
  board,
  main,
  active,
  onOpen,
  onRename,
  onDelete,
}: {
  board: HomeBoard;
  /** FR3: the main board always exists. */
  main: boolean;
  active: boolean;
  onOpen: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const item = useSortableItem(board.id);
  const [name, setName] = useState(board.name);

  function commit() {
    const trimmed = name.trim();
    if (!trimmed) setName(board.name);
    else if (trimmed !== board.name) onRename(trimmed);
  }

  return (
    <li
      ref={item.ref}
      style={item.style}
      data-dragging={item.isDragging}
      className={cn(
        "relative flex min-h-36 flex-col justify-between gap-3 border p-3 data-[dragging=true]:z-10 data-[dragging=true]:border-ring",
        active ? "border-input bg-muted" : "border-border/60 bg-card",
      )}
    >
      <div className="flex items-center gap-1.5">
        <DragHandle label={`Move ${board.name}`} handle={item.handle} />
        <Input
          aria-label="Board name"
          value={name}
          maxLength={40}
          className="h-8 min-w-0 flex-1 font-medium"
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
        />
      </div>
      <p className="flex flex-wrap gap-x-2 text-xs text-dim-foreground">
        {main && <span>Main board · holds the top grid</span>}
        {active && <span className="text-foreground">Open now</span>}
        {!main && !active && (
          <span>
            {board.widgets.length} {board.widgets.length === 1 ? "widget" : "widgets"}
          </span>
        )}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Open ${board.name}`}
          disabled={active}
          onClick={onOpen}
          className="flex-1 justify-start"
        >
          <ArrowRight />
          Open
        </Button>
        {/* Disabled buttons get no pointer events: the tooltip sits on a wrapper. */}
        <Tooltip content={main ? "The main board cannot be deleted" : "Delete"}>
          <span className="inline-flex">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              aria-label={`Delete ${board.name}`}
              disabled={main}
              onClick={onDelete}
            >
              <Trash2 />
            </Button>
          </span>
        </Tooltip>
      </div>
    </li>
  );
}
